<template>
  <div class="family-app w-full bg-slate-50">
    <!-- 主内容：头部固定，中间区域滚动 -->
    <div class="family-main">
      <div v-show="activeTab === 'home'" class="family-tab-panel">
        <FamilyHomeTab
          :user-name="profile.name || '用户'"
          class="family-tab-panel-inner"
        />
      </div>

      <div v-show="activeTab === 'orders'" class="family-tab-panel">
        <header
          class="family-tab-header bg-slate-50/95 backdrop-blur-sm border-b border-slate-100 px-4 pt-4 pb-3"
          :style="{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }"
        >
          <h1 class="text-lg font-bold text-slate-900 mb-3">我的订单</h1>
          <div class="flex gap-2 overflow-x-auto -mx-1 px-1 scrollbar-hide">
            <button
              v-for="tab in orderStatusTabs"
              :key="tab.value"
              type="button"
              class="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
              :class="orderFilter === tab.value ? 'bg-blue-50 text-family-blue' : 'text-slate-500'"
              @click="setOrderFilter(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </header>

        <div class="family-tab-scroll px-4 pb-4">
          <div v-if="ordersLoading" class="py-16 text-center text-sm text-slate-400">
            加载中...
          </div>
          <div v-else-if="orders.length === 0" class="py-16 text-center text-sm text-slate-400">
            暂无相关订单
          </div>

          <article
            v-for="order in orders"
            :key="order.id"
            class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)] cursor-pointer active:bg-slate-50 transition-colors"
            @click="goOrderDetail(order.id)"
          >
            <div class="flex items-center justify-between gap-2 mb-3">
              <span class="text-xs text-slate-400">订单 {{ order.orderNo }}</span>
              <span
                class="flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                :class="statusStyle(order.status).tag"
              >
                {{ order.statusLabel || statusStyle(order.status).label }}
              </span>
            </div>

            <div class="flex items-start gap-3 mb-3">
              <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-family-blue">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-bold text-slate-900 truncate">{{ order.hospital }}</p>
                <p class="text-xs text-slate-500 mt-1 leading-relaxed">
                  {{ order.department }}
                  <span class="text-red-500 font-medium"> · {{ order.bedNo }}</span>
                </p>
              </div>
            </div>

            <div class="flex items-center justify-between gap-2 pt-3 border-t border-slate-50">
              <div v-if="order.caregiver" class="flex items-center gap-2 min-w-0">
                <div
                  class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                  :class="order.caregiver.avatarBg"
                >
                  {{ order.caregiver.name.charAt(0) }}
                </div>
                <span class="text-xs text-slate-600 truncate">
                  已指派 {{ order.caregiver.name }} · {{ order.caregiver.level }}
                </span>
              </div>
              <div v-else-if="order.preferredCaregiver" class="flex items-center gap-2 min-w-0">
                <div
                  class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                  :class="order.preferredCaregiver.avatarBg"
                >
                  {{ order.preferredCaregiver.name.charAt(0) }}
                </div>
                <span class="text-xs text-amber-600 truncate">
                  意向 {{ order.preferredCaregiver.name }} · 待确认
                </span>
              </div>
              <div v-else class="flex items-center gap-1.5 min-w-0">
                <span class="text-xs text-sky-600">🎲 随机分配 · 待派单</span>
              </div>
              <span class="flex-shrink-0 text-xs text-slate-400">{{ order.dateRange }}</span>
            </div>
            <p v-if="order.status === 'done' && order.reviewed" class="text-[11px] text-slate-400 mt-2 text-right">
              已评价
            </p>
            <p v-else-if="order.status === 'done' && order.caregiver" class="text-[11px] text-family-blue mt-2 text-right">
              点击评价护工 →
            </p>
          </article>
        </div>
      </div>

      <!-- Tab 3: 个人中心 -->
      <div v-show="activeTab === 'profile'" class="family-tab-panel family-tab-panel--scroll">
        <!-- 蓝色头部 -->
        <div
          class="px-4 pb-8 rounded-b-3xl bg-cover bg-center"
          :class="profile.profileBg ? '' : 'bg-family-blue'"
          :style="profileHeaderStyle"
        >
          <div class="flex items-center justify-end mb-4">
            <button
              type="button"
              class="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center text-white/90"
              aria-label="设置"
              @click="goSettings"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          <div class="flex items-center gap-4">
            <div
              v-if="profile.avatar"
              class="w-14 h-14 rounded-2xl overflow-hidden border border-white/30 shadow"
            >
              <img :src="profile.avatar" alt="" class="w-full h-full object-cover" />
            </div>
            <div
              v-else
              class="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-white text-xl font-bold"
            >
              {{ avatarLetter }}
            </div>
            <div>
              <p class="text-lg font-bold text-white">{{ profile.name }}</p>
              <p class="text-sm text-white/80 mt-0.5">{{ profile.mobile }}</p>
            </div>
          </div>
        </div>

        <div class="px-4 -mt-5 pb-4">
          <!-- 快捷入口 -->
          <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
            <div class="grid grid-cols-4 gap-2">
              <button
                v-for="item in quickActions"
                :key="item.label"
                type="button"
                class="flex flex-col items-center gap-2 py-1"
                @click="item.action && item.action()"
              >
                <div
                  class="w-11 h-11 rounded-xl flex items-center justify-center"
                  :class="item.bg"
                >
                  <span v-html="item.icon" class="w-5 h-5 flex items-center justify-center" />
                </div>
                <span class="text-[11px] text-slate-600 text-center leading-tight">{{ item.label }}</span>
              </button>
            </div>
          </div>

          <!-- 会员中心 -->
          <button
            type="button"
            class="w-full bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)] flex items-center gap-3 text-left"
          >
            <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-caregiver-green">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm2.7-2h8.6l.9-5.4-2.1 2.2L12 8l-3.1 2.8-2.1-2.2L7.7 14z" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-slate-800">家属会员中心</p>
              <p class="text-xs text-slate-400 mt-0.5 truncate">长期住院家庭可享受陪护季度套餐</p>
            </div>
            <svg class="flex-shrink-0 w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <!-- 设置列表 -->
          <div class="bg-white rounded-xl overflow-hidden shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
            <button
              v-for="(item, idx) in settingsMenus"
              :key="item.label"
              type="button"
              class="w-full flex items-center justify-between px-4 py-3.5 text-sm transition-colors active:bg-slate-50"
              :class="[
                idx < settingsMenus.length - 1 ? 'border-b border-slate-50' : '',
                item.danger ? 'text-red-500' : 'text-slate-700',
              ]"
              @click="item.action && item.action()"
            >
              <span class="flex items-center gap-3">
                <span v-html="item.icon" class="w-5 h-5 text-slate-400 flex items-center justify-center" />
                {{ item.label }}
              </span>
              <svg class="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <p class="text-center text-xs text-slate-300 mt-6 pb-4">安心陪护 v0.1.0 · 2026</p>
        </div>
      </div>
    </div>

    <!-- 首页悬浮球：发起陪护 -->
    <button
      v-if="activeTab === 'home'"
      type="button"
      class="family-fab fixed z-40 flex flex-col items-center justify-center rounded-full bg-family-blue text-white shadow-[0_8px_28px_rgb(0,130,212,0.45)] transition-transform duration-200 active:scale-95"
      :style="{ bottom: fabBottom, right: '1rem' }"
      aria-label="发起陪护需求"
      @click="goRequest"
    >
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
      </svg>
      <span class="text-[10px] font-semibold mt-0.5 leading-none">陪护</span>
    </button>

    <!-- 底部 Tab 导航（固定底部） -->
    <nav
      class="family-bottom-nav bg-white border-t border-slate-100 shadow-[0_-4px_20px_rgb(0,0,0,0.04)]"
      :style="{ paddingBottom: 'env(safe-area-inset-bottom)' }"
    >
      <div class="flex items-stretch h-14">
        <button
          v-for="tab in bottomTabs"
          :key="tab.key"
          type="button"
          class="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors duration-200"
          :class="activeTab === tab.key ? 'text-family-blue' : 'text-slate-400'"
          @click="onTabClick(tab.key)"
        >
          <span v-html="tab.icon" class="w-6 h-6 flex items-center justify-center" />
          <span class="text-[11px] font-medium">{{ tab.label }}</span>
        </button>
      </div>
    </nav>

    <FamilyMobileConfirm
      :visible.sync="logoutVisible"
      title="退出登录"
      message="确定要退出当前账号吗？"
      confirm-text="确认退出"
      cancel-text="取消"
      :danger="true"
      @confirm="confirmLogout"
    />
  </div>
</template>

<script>
const TAB_ICONS = {
  home: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>',
  orders: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>',
  profile: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
};

const STATUS_MAP = {
  pending: { label: "待派单", tag: "bg-red-50 text-red-500" },
  serving: { label: "服务中", tag: "bg-emerald-50 text-emerald-600" },
  pending_confirm: { label: "待确认完工", tag: "bg-orange-50 text-orange-500" },
  done: { label: "已完工", tag: "bg-slate-100 text-slate-500" },
};

const QUICK_ACTION_ICONS = {
  orders: '<svg class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>',
  archive: '<svg class="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>',
  coupon: '<svg class="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/></svg>',
  service: '<svg class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>',
};

const SETTINGS_ICONS = {
  shield: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>',
  bell: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>',
  faq: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
  doc: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
  info: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
  logout: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>',
};

import { familyAuth } from "./familyAuth";
import { familyOrderApi } from "./familyOrderApi";
import { familyUserApi } from "./familyUserApi";
import { buildFamilyTabQuery, familySubRouteQuery } from "./familyNavigation";
import FamilyHomeTab from "./FamilyHomeTab.vue";
import FamilyMobileConfirm from "./FamilyMobileConfirm.vue";

export default {
  name: "FamilyApp",
  components: { FamilyHomeTab, FamilyMobileConfirm },
  data() {
    return {
      activeTab: "home",
      orderFilter: "all",
      orders: [],
      ordersLoading: false,
      profile: {
        name: "用户",
        mobile: "",
        avatar: "",
        profileBg: "",
      },
      logoutVisible: false,
      quickActions: [
        { label: "我的订单", icon: QUICK_ACTION_ICONS.orders, bg: "bg-blue-50", action: null },
        { label: "陪护人档案", icon: QUICK_ACTION_ICONS.archive, bg: "bg-emerald-50", action: null },
        { label: "我的优惠券", icon: QUICK_ACTION_ICONS.coupon, bg: "bg-red-50", action: null },
        { label: "联系客服", icon: QUICK_ACTION_ICONS.service, bg: "bg-blue-50", action: null },
      ],
      settingsMenus: [
        { label: "账号与安全", icon: SETTINGS_ICONS.shield, action: null },
        { label: "通知设置", icon: SETTINGS_ICONS.bell, action: null },
        { label: "常见问题", icon: SETTINGS_ICONS.faq, action: null },
        { label: "服务协议与隐私", icon: SETTINGS_ICONS.doc, action: null },
        { label: "关于安心陪护", icon: SETTINGS_ICONS.info, action: null },
        { label: "退出登录", icon: SETTINGS_ICONS.logout, action: null, danger: true },
      ],
      orderStatusTabs: [
        { label: "全部", value: "all" },
        { label: "待派单", value: "pending" },
        { label: "服务中", value: "serving" },
        { label: "已完工", value: "done" },
      ],
      bottomTabs: [
        { key: "home", label: "首页", icon: TAB_ICONS.home },
        { key: "orders", label: "订单", icon: TAB_ICONS.orders },
        { key: "profile", label: "我的", icon: TAB_ICONS.profile },
      ],
    };
  },
  computed: {
    avatarLetter() {
      return (this.profile.name || "用").charAt(0);
    },
    fabBottom() {
      return "calc(4.25rem + env(safe-area-inset-bottom))";
    },
    profileHeaderStyle() {
      const style = {
        paddingTop: "calc(1rem + env(safe-area-inset-top))",
      };
      if (this.profile.profileBg) {
        style.backgroundImage = `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.25)), url(${this.profile.profileBg})`;
      }
      return style;
    },
  },
  watch: {
    "$route": {
      immediate: true,
      handler(to) {
        if (to.path !== "/family") return;
        const query = to.query || {};
        if (query.tab === "orders" || query.tab === "profile") {
          this.activeTab = query.tab;
        } else {
          this.activeTab = "home";
        }
        if (query.filter) {
          this.orderFilter = query.filter;
        }
        this.$nextTick(() => {
          if (this.activeTab === "orders") {
            this.refreshOrders();
          }
          if (this.activeTab === "profile" && familyAuth.isLoggedIn()) {
            this.loadRemoteProfile();
          }
        });
      },
    },
    orderFilter() {
      this.refreshOrders();
    },
    activeTab(tab) {
      if (tab === "orders") {
        this.refreshOrders();
      }
      if (tab === "profile" && familyAuth.isLoggedIn()) {
        this.loadRemoteProfile();
      }
    },
  },
  created() {
    this.syncProfile();
    this.quickActions[0].action = () => {
      this.onTabClick("orders");
    };
    this.quickActions[1].action = () => {
      if (!this.ensureFamilyLogin("/family/patients")) return;
      this.$router.push({
        path: "/family/patients",
        query: familySubRouteQuery("profile"),
      });
    };
    this.quickActions[3].action = () => {
      window.location.href = "tel:4008880000";
    };
    this.settingsMenus[0].action = () => {
      if (!this.ensureFamilyLogin("/family/settings")) return;
      this.$router.push({
        path: "/family/settings",
        query: familySubRouteQuery("profile"),
      });
    };
    this.settingsMenus[this.settingsMenus.length - 1].action = () => {
      this.handleLogout();
    };
  },
  mounted() {
    document.title = "安心陪护 - 用户端";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    isFamilyTokenValid() {
      if (!familyAuth.isLoggedIn()) return false;
      const token = familyAuth.getToken();
      if (!token) return false;
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload && payload.exp && Number(payload.exp) < now) {
          return false;
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    ensureFamilyLogin(targetPath) {
      if (this.isFamilyTokenValid()) {
        return true;
      }
      familyAuth.logout();
      this.syncProfile();
      this.$message.warning("请先登录后再使用该功能");
      this.$router.push({
        path: "/family/login",
        query: targetPath ? { redirect: targetPath } : undefined,
      });
      return false;
    },
    async refreshOrders() {
      if (!familyAuth.isLoggedIn()) return;
      this.ordersLoading = true;
      try {
        this.orders = await familyOrderApi.list(this.orderFilter);
      } catch (e) {
        const msg = (e && e.message) || "";
        if (msg !== "认证失败" && msg !== "未登录") {
          this.$message.error((e && e.message) || "订单加载失败");
        }
        this.orders = [];
      } finally {
        this.ordersLoading = false;
      }
    },
    goOrderDetail(id) {
      this.$router.push({
        path: `/family/order/${id}`,
        query: familySubRouteQuery(this.activeTab, this.orderFilter),
      });
    },
    goRequest() {
      if (!this.ensureFamilyLogin("/family/request")) return;
      this.$router.push({
        path: "/family/request",
        query: familySubRouteQuery(this.activeTab),
      });
    },
    setOrderFilter(value) {
      this.orderFilter = value;
      if (this.activeTab === "orders") {
        this.$router.replace({
          path: "/family",
          query: buildFamilyTabQuery("orders", value),
        });
      }
    },
    onTabClick(tabKey) {
      if ((tabKey === "orders" || tabKey === "profile") && !this.ensureFamilyLogin("/family")) {
        return;
      }
      this.activeTab = tabKey;
      const query = buildFamilyTabQuery(tabKey, tabKey === "orders" ? this.orderFilter : undefined);
      this.$router.replace({ path: "/family", query });
    },
    goSettings() {
      if (!this.ensureFamilyLogin("/family/settings")) return;
      this.$router.push({
        path: "/family/settings",
        query: familySubRouteQuery("profile"),
      });
    },
    syncProfile() {
      if (!familyAuth.isLoggedIn()) {
        this.profile.name = "用户";
        this.profile.mobile = "";
        this.profile.avatar = "";
        this.profile.profileBg = "";
        return;
      }
      const user = familyAuth.getUser();
      if (user) {
        this.profile.name = user.nickname;
        this.profile.mobile = user.mobileMasked || user.mobile;
        this.profile.avatar = user.avatar || "";
        this.profile.profileBg = user.profileBg || "";
      }
    },
    async loadRemoteProfile() {
      try {
        const data = await familyUserApi.profile();
        familyAuth.patchLocalUser({
          nickname: data.nickname,
          avatar: data.avatar,
          profileBg: data.profileBg,
          mobileMasked: data.mobileMasked,
        });
        this.syncProfile();
      } catch (e) {
        // 静默失败，使用本地缓存
      }
    },
    handleLogout() {
      this.logoutVisible = true;
    },
    confirmLogout() {
      this.logoutLoading = true;
      familyAuth.logout();
      this.logoutVisible = false;
      this.logoutLoading = false;
      this.syncProfile();
      this.$router.replace("/family/login");
    },
    statusStyle(status) {
      return STATUS_MAP[status] || STATUS_MAP.pending;
    },
  },
};
</script>

<style scoped>
.family-app {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
  max-width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.family-main {
  flex: 1 1 0;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

.family-tab-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: calc(3.5rem + env(safe-area-inset-bottom));
  left: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.family-tab-panel--scroll {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.family-tab-panel-inner {
  height: 100%;
  min-height: 0;
}

.family-tab-header {
  flex-shrink: 0;
}

.family-tab-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.family-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 40;
  padding-bottom: env(safe-area-inset-bottom);
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.family-fab {
  width: 3.75rem;
  height: 3.75rem;
}
</style>
